import { useCallback, useEffect, useState } from 'react'
import { api } from '../../api/client'
import { isIos, isStandalone } from '../../components/InstallHint'

/** base64url VAPID anahtarını Uint8Array'e çevirir — PushManager bu formatı istiyor. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
}

const bufToB64 = (buf: ArrayBuffer | null): string =>
  buf ? btoa(String.fromCharCode(...new Uint8Array(buf))) : ''

export function usePush() {
  const supported =
    typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window
  const [subscribed, setSubscribed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState('Kontrol ediliyor…')

  /**
   * iPhone/iPad'de Web Push YALNIZCA ana ekrana eklenmiş uygulamada çalışıyor
   * (iOS 16.4+). Safari sekmesinde `PushManager` var gibi görünüyor ama abone
   * olma denemesi anlaşılmaz bir hatayla düşüyor. Kullanıcıya "bilinmeyen hata"
   * göstermek yerine gerçek sebebi söylüyoruz.
   */
  const iosNeedsInstall = isIos() && !isStandalone()

  useEffect(() => {
    if (iosNeedsInstall) {
      setStatus('iPhone/iPad\'de bildirimler yalnızca ana ekrana eklenmiş uygulamada çalışıyor. ' +
        'Safari\'de Paylaş → Ana Ekrana Ekle yap, sonra paneli o ikondan açıp buraya dön.')
      return
    }
    if (!supported) {
      setStatus('Bu tarayıcı push bildirimini desteklemiyor. Sayaç yine çalışır — ses ve başlık uyarısı aktif.')
      return
    }
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        setSubscribed(!!sub)
        setStatus(sub ? 'Bu cihaz kayıtlı ✓' : 'Bu cihaz kayıtlı değil.')
      })
      .catch(() => setStatus('Service worker hazır değil. Sayfayı yenilemeyi dene.'))
  }, [supported, iosNeedsInstall])

  const subscribe = useCallback(async () => {
    setBusy(true)
    try {
      const perm = await Notification.requestPermission()
      if (perm !== 'granted') {
        setStatus('İzin verilmedi. Tarayıcı ayarlarından site bildirimlerini açman gerekiyor.')
        return
      }
      const { publicKey } = await api.pushKey()
      if (!publicKey) {
        setStatus('Sunucuda VAPID anahtarı tanımlı değil (Push:PublicKey).')
        return
      }
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      })
      const json = sub.toJSON() as { keys?: { p256dh?: string; auth?: string } }
      await api.pushSubscribe(
        sub.endpoint,
        json.keys?.p256dh ?? bufToB64(sub.getKey('p256dh')),
        json.keys?.auth ?? bufToB64(sub.getKey('auth')),
        navigator.userAgent.slice(0, 60),
      )
      setSubscribed(true)
      setStatus('Bu cihaz kayıtlı ✓')
    } catch (e) {
      setStatus(`Kaydedilemedi: ${e instanceof Error ? e.message : 'bilinmeyen hata'}`)
    } finally {
      setBusy(false)
    }
  }, [])

  const unsubscribe = useCallback(async () => {
    setBusy(true)
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        await api.pushUnsubscribe(sub.endpoint)
        await sub.unsubscribe()
      }
      setSubscribed(false)
      setStatus('Bildirimler kapatıldı.')
    } finally {
      setBusy(false)
    }
  }, [])

  const test = useCallback(async () => {
    setBusy(true)
    try {
      const { sent } = await api.pushTest()
      setStatus(sent > 0 ? `Test gönderildi (${sent} cihaz).` : 'Kayıtlı cihaz bulunamadı.')
    } catch (e) {
      setStatus(`Test başarısız: ${e instanceof Error ? e.message : 'bilinmeyen hata'}`)
    } finally {
      setBusy(false)
    }
  }, [])

  return { supported: supported && !iosNeedsInstall, iosNeedsInstall,
    subscribed, busy, status, subscribe, unsubscribe, test }
}
